import { ReactNode } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Trans } from "react-i18next";

import { RootState } from "../../state/store";
import { useCanWrite } from "../../utilities/helpers/roleUtilities";
import { noResultsHint } from "../../utilities/helpers/noResultsHint";
import { WriteKey } from "../../utilities/helpers/writeVocabulary.generated";

type ShiftTableNoResultsProps = {
  empty: string;
  entity: string;
  howToAdd: ReactNode;
  addWrite: WriteKey;
};

const ShiftTableNoResults: React.FC<ShiftTableNoResultsProps> = ({ empty, entity, howToAdd, addWrite }) => {
  const canAdd = useCanWrite(addWrite);
  const isShiftLocked = useSelector((s: RootState) => s.shift.isLocked);
  const canToggleLock = useSelector((s: RootState) => s.shift.canToggleLock);
  const hint = noResultsHint({ canAdd, isShiftLocked, canToggleLock });

  return (
    <div className="text-center flex flex-col gap-1 py-4">
      <span className="text-sm text-theme-text">{empty}</span>
      <div className="flex flex-col items-center justify-center gap-6">
        <div className="h-[2px] w-full max-w-[20rem] bg-theme-table-border" />
        {hint === "add" && <p className="text-base text-theme-text max-w-[45rem]">{howToAdd}</p>}
        {hint === "unlock" && (
          <p className="text-base text-theme-text max-w-[45rem]">
            <Trans
              i18nKey="shiftNoResults.unlock"
              values={{ entity }}
              components={{
                bold: <span className="font-bold" />,
                home: (
                  <Link
                    className="mx-1 inline-flex items-center gap-1 px-2 font-bold border border-theme-info bg-theme-surface-2 hover:bg-theme-info-hover hover:text-white rounded transition duration-150 ease-in-out text-theme-info"
                    to='/'
                  />
                ),
                icon: <FontAwesomeIcon className="text-xs" icon={faExternalLinkAlt} />,
              }}
            />
          </p>
        )}
        {hint === "ask-admin" && (
          <p className="text-base text-theme-text max-w-[45rem]">
            <Trans i18nKey="shiftNoResults.askAdmin" values={{ entity }} components={{ bold: <span className="font-bold" /> }} />
          </p>
        )}
      </div>
    </div>
  );
};

export default ShiftTableNoResults;
